/**
 * @fullvsl/app-shell/api-client — lightweight HTTP client + API availability detection (FUL-244).
 *
 * When the app shell is served by the FullVSL backend server (same origin),
 * the `/api/v1/health` endpoint is reachable. This enables "live mode": VSL
 * CRUD syncs to the backend and analytics use the live HttpAnalyticsSource.
 *
 * When the health check fails (static-only host like Cloudflare Pages, or the
 * backend is down), the app gracefully degrades to its localStorage mock store
 * + fixture analytics — the documented static-first fallback.
 */
import type {
  ConnectRequest,
  ConnectionTestRequest,
  ConnectionTestResult,
  CreateVslRequest,
  CredentialAuditEvent,
  TenantConnection,
  VslProject,
} from "../../contracts/src/contracts";

/** The API base URL (same-origin by default when served by the backend). */
export const API_BASE = "/api/v1";

export interface ApiHealthInfo {
  available: boolean;
  origin: string;
}

let cachedHealth: ApiHealthInfo | null = null;

/**
 * Probe the backend health endpoint. Returns whether the API is live.
 * Cached after the first call — the availability doesn't change mid-session.
 *
 * FUL-272: a static host (Cloudflare Pages) serves the SPA `index.html` with
 * HTTP 200 for unknown paths, so `res.ok` alone is a false positive. Require a
 * JSON content type AND a parseable health body before reporting available.
 */
export async function detectApi(): Promise<ApiHealthInfo> {
  if (cachedHealth) return cachedHealth;
  try {
    const res = await fetch(`${API_BASE}/health`, {
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(2000),
    });
    cachedHealth = {
      available: res.ok && isJsonResponse(res) && (await hasValidHealthShape(res)),
      origin: window.location.origin,
    };
  } catch {
    cachedHealth = { available: false, origin: window.location.origin };
  }
  return cachedHealth;
}

/** True when the response advertises a JSON content type (not an HTML SPA fallback). */
function isJsonResponse(res: Response): boolean {
  const ct = res.headers.get("content-type") ?? "";
  return ct.toLowerCase().includes("application/json");
}

/**
 * Consume + validate the health body. The backend health route returns a JSON
 * object (e.g. `{ status: "ok", ... }`); an HTML SPA fallback parses to a throw.
 * Reads the body once and is defensive: any non-object body fails closed.
 */
async function hasValidHealthShape(res: Response): Promise<boolean> {
  try {
    const body = await res.clone().json();
    return typeof body === "object" && body !== null;
  } catch {
    return false;
  }
}

/** Synchronous access to the cached health result (null before detection). */
export function getCachedApiHealth(): ApiHealthInfo | null {
  return cachedHealth;
}

/** Force-reset the cache (tests). */
export function resetApiCache(): void {
  cachedHealth = null;
}

/* ---------------------------------------------------------------------------------
 * VSL CRUD — thin fetch wrappers that hit the live backend.
 * All methods return promises; the store layer handles async-to-sync bridging.
 * --------------------------------------------------------------------------------- */

export async function apiListVsls(): Promise<VslProject[]> {
  const res = await fetch(`${API_BASE}/vsls`, { headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(`list failed: ${res.status}`);
  return (await readJsonList<VslProject>(res));
}

/**
 * Parse a JSON array response, rejecting HTML SPA fallbacks (FUL-272).
 * On a static host, unknown paths return `index.html` with HTTP 200; without a
 * content-type guard, `res.json()` would throw an opaque parse error. Fail with
 * a clear, catchable error so callers fall back to offline mode cleanly.
 */
async function readJsonList<T>(res: Response): Promise<T[]> {
  if (!isJsonResponse(res)) {
    throw new Error("non-JSON response (offline / SPA fallback)");
  }
  const body = (await res.json()) as unknown;
  return Array.isArray(body) ? (body as T[]) : [];
}

export async function apiCreateVsl(req: CreateVslRequest): Promise<VslProject> {
  const res = await fetch(`${API_BASE}/vsls`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) throw new Error(`create failed: ${res.status}`);
  return (await res.json()) as VslProject;
}

export async function apiUpdateVsl(
  id: string,
  patch: Partial<VslProject>,
): Promise<VslProject> {
  const res = await fetch(`${API_BASE}/vsls/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`update failed: ${res.status}`);
  return (await res.json()) as VslProject;
}

export async function apiDeleteVsl(id: string): Promise<boolean> {
  const res = await fetch(`${API_BASE}/vsls/${id}`, { method: "DELETE" });
  return res.ok;
}

export async function apiUpdateConfig(
  id: string,
  config: Record<string, unknown>,
): Promise<VslProject> {
  const res = await fetch(`${API_BASE}/vsls/${id}/config`, {
    method: "PUT",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(config),
  });
  if (!res.ok) throw new Error(`config update failed: ${res.status}`);
  return (await res.json()) as VslProject;
}

/* ---------------------------------------------------------------------------------
 * Integration connections — per-subaccount Full Funnel credential lifecycle (FUL-128/246/254).
 * The secret is submitted once and never persisted client-side; only RedactedCredential
 * (mask + hash) ever appears in a response. Implemented in backend/src/api-routes.ts.
 * --------------------------------------------------------------------------------- */

const CONNECTIONS_BASE = `${API_BASE}/integration/connections`;

/** List all per-subaccount connections (redacted, excludes revoked). */
export async function apiListConnections(): Promise<TenantConnection[]> {
  const res = await fetch(CONNECTIONS_BASE, { headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(`connections list failed: ${res.status}`);
  return (await res.json()) as TenantConnection[];
}

/** Connect (or rotate) a credential for a location. Auto-tests on connect. */
export async function apiConnect(req: ConnectRequest): Promise<TenantConnection> {
  const res = await fetch(CONNECTIONS_BASE, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) throw new Error(`connect failed: ${res.status}`);
  return (await res.json()) as TenantConnection;
}

/** Get one connection by locationId (redacted). */
export async function apiGetConnection(locationId: string): Promise<TenantConnection> {
  const res = await fetch(`${CONNECTIONS_BASE}/${locationId}`, {
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`get connection failed: ${res.status}`);
  return (await res.json()) as TenantConnection;
}

/** Probe scopes + location validity for a connection. */
export async function apiTestConnection(
  locationId: string,
  opts?: ConnectionTestRequest,
): Promise<ConnectionTestResult> {
  const res = await fetch(`${CONNECTIONS_BASE}/${locationId}/test`, {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(opts ?? {}),
  });
  if (!res.ok) throw new Error(`connection test failed: ${res.status}`);
  return (await res.json()) as ConnectionTestResult;
}

/** Revoke (disconnect) a location's credential. Idempotent. */
export async function apiRevokeConnection(locationId: string): Promise<boolean> {
  const res = await fetch(`${CONNECTIONS_BASE}/${locationId}`, { method: "DELETE" });
  return res.ok || res.status === 204;
}

/** Credential lifecycle audit trail for a location (append-only, never the secret). */
export async function apiConnectionAudit(
  locationId: string,
): Promise<CredentialAuditEvent[]> {
  const res = await fetch(`${CONNECTIONS_BASE}/${locationId}/audit`, {
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`connection audit failed: ${res.status}`);
  return (await res.json()) as CredentialAuditEvent[];
}
